import React, { useState } from 'react';
import { Pencil, Send, Trash2, Loader, CheckCircle } from 'lucide-react';
import { useReports } from '../../contexts/ReportsContext';
import { OvertimeEditForm } from './OvertimeEditForm';

// Проверяем, можно ли ещё редактировать/удалять отчёт:
// доступно до 0:00 того дня, когда он был создан (submittedAt)
const isEditable = (report) => !report.submittedEarly;

export const OvertimeMobileCard = ({ report, darkMode }) => {
  const date = new Date(report.date).toLocaleDateString('ru-RU');
  const { deleteOvertimeReport, submitOvertimeEarly } = useReports();

  const [showEditForm, setShowEditForm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [submittingEarly, setSubmittingEarly] = useState(false);

  const canEdit = isEditable(report);
  const isSubmittedEarly = report.submittedEarly;

  const getWorkTypeColor = () => {
    if (report.workType?.includes('выходной') || report.workType?.includes('Возврат')) {
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300';
    }
    return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300';
  };

  const handleDelete = async () => {
    if (!window.confirm('Удалить этот отчёт? Это действие необратимо.')) return;
    setDeleting(true);
    const result = await deleteOvertimeReport(report.id);
    setDeleting(false);
    if (!result.success) alert(`Ошибка удаления: ${result.error}`);
  };

  const handleSubmitEarly = async () => {
    if (!window.confirm('Отправить отчёт в таблицу досрочно? После этого редактирование и удаление будут недоступны.')) return;
    setSubmittingEarly(true);
    const result = await submitOvertimeEarly(report.id);
    setSubmittingEarly(false);
    if (!result.success) alert(`Ошибка: ${result.error}`);
  };

  return (
    <>
      <div className={`p-4 mb-3 rounded-lg border ${
        darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
      }`}>
        <div className="flex justify-between items-start mb-2">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Дата</p>
            <p className="text-sm font-medium">{date}</p>
          </div>
          <span className={`px-2 py-1 text-xs rounded-full ${getWorkTypeColor()}`}>
            {report.workType || 'Не указан'}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Время</p>
            <p className="text-sm">{report.startTime} – {report.endTime}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Часы</p>
            <p className="text-sm font-medium">{report.totalHours} ч.</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Переработка</p>
            <p className={`text-sm font-bold ${
              report.overtimeHours > 0
                ? 'text-red-600 dark:text-red-400'
                : 'text-gray-500 dark:text-gray-400'
            }`}>
              {report.overtimeHours} ч.
            </p>
          </div>
        </div>

        <div className="mb-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Проект</p>
          <p className="text-sm break-words">{report.project}</p>
        </div>

        {/* Статус досрочной отправки */}
        {isSubmittedEarly && (
          <div className={`flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full w-fit mb-3 ${
            darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700'
          }`}>
            <CheckCircle className="w-3.5 h-3.5" />
            Отправлено в таблицу
          </div>
        )}

        {/* Кнопки действий */}
        {!isSubmittedEarly && (
          <div className="flex gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
            {canEdit && (
              <>
                <button
                  onClick={() => setShowEditForm(true)}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                    darkMode
                      ? 'bg-blue-900/40 text-blue-300 hover:bg-blue-900/70'
                      : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                  }`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Редактировать
                </button>

                <button
                  onClick={handleSubmitEarly}
                  disabled={submittingEarly}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                    darkMode
                      ? 'bg-green-900/40 text-green-300 hover:bg-green-900/70'
                      : 'bg-green-50 text-green-700 hover:bg-green-100'
                  } ${submittingEarly ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  {submittingEarly
                    ? <Loader className="w-3.5 h-3.5 animate-spin" />
                    : <Send className="w-3.5 h-3.5" />
                  }
                  Досрочно
                </button>

                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                    darkMode
                      ? 'bg-red-900/40 text-red-300 hover:bg-red-900/70'
                      : 'bg-red-50 text-red-700 hover:bg-red-100'
                  } ${deleting ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  {deleting
                    ? <Loader className="w-3.5 h-3.5 animate-spin" />
                    : <Trash2 className="w-3.5 h-3.5" />
                  }
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {showEditForm && (
        <OvertimeEditForm report={report} onClose={() => setShowEditForm(false)} />
      )}
    </>
  );
};
